import React, { useMemo, useState } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";

import { ThirdParty } from "Constants/integrationThirdParties";
import Button, { ButtonWrapper } from "ds/Button";
import Modal from "ds/Modal";
import { capitalize } from "Libs/utils";
import { selectFeatureUsageBasedBillingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { deleteIntegration } from "Reducers/integration";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import IntegrationItem from "../IntegrationItem";
import { ActionBtn } from "../IntegrationItem/IntegrationItem.styles";

import * as S from "./IntegrationCategory.styles";

type IntegrationCategoryProps = {
  name: string;
  list: ThirdParty[];
  getEnablement?: (e: ThirdParty) => boolean;
};

const getCategoryTextInfo = ({ isUsageBilling, categoryName }) => {
  let learnMoreLinkId = "links.observability-suite-features";
  if (isUsageBilling && categoryName === "observability_suite") {
    categoryName = "usage_billed";
    learnMoreLinkId = "links.documentation.integrations";
  }
  return { learnMoreLinkId, categoryName };
};

const IntegrationCategory = ({
  name,
  list,
  getEnablement
}: IntegrationCategoryProps) => {
  const [modal, setModal] = useState<{ open: boolean; type?: string }>({
    open: false
  });
  const isUsageBilling = useAppSelector(selectFeatureUsageBasedBillingEnabled);
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  const sortedList = list.sort((a, b) => {
    const typeA = a.type.toLowerCase();
    const typeB = b.type.toLowerCase();
    return typeA < typeB ? -1 : typeA > typeB ? 1 : 0;
  });

  const integrations = useAppSelector(state => {
    return !projectId
      ? undefined
      : Object.values(state.integration.data[projectId] ?? {}).filter(int => {
          return list.find(elt => elt.type === int?.type);
        });
  });

  const { learnMoreLinkId, categoryName } = useMemo(
    () => getCategoryTextInfo({ isUsageBilling, categoryName: name }),
    [isUsageBilling, name]
  );

  if (!projectId) return null;

  const isGitConnected = (elt: ThirdParty) => {
    if (elt.category !== "git") return false;
    return integrations?.some(int => int?.type === elt.type);
  };

  const getConnectedGit = () => {
    if (name !== "git") return;
    return integrations?.find(elt =>
      list.map<string | undefined>(cat => cat.type).includes(elt?.type)
    );
  };

  const currentGit = getConnectedGit();

  const onSelect = (item: ThirdParty) => {
    if (name !== "git" || !currentGit) {
      const type = item.type.replace(".", "_");
      navigate(`${type}/configure`);
    } else {
      setModal({ open: true, type: item.type });
    }
  };

  const onReplaceConfirm = async (e: React.MouseEvent) => {
    if (e) e.preventDefault();
    const result = await dispatch(
      deleteIntegration({ projectId, integration: currentGit })
    );
    if (result.type === "app/integration/delete/fulfilled") {
      navigate(`${modal.type}/configure`);
    }
  };

  const getCanEditGitConnection = (elt: ThirdParty) => {
    return elt?.category == "git" && currentGit
      ? currentGit?.hasLink("#edit")
      : true;
  };

  return (
    <S.Wrapper>
      <S.Header>
        <S.Title>
          {intl.formatMessage({
            id: `integration.setup.categories.${name}.title`,
            defaultMessage: name
          })}
        </S.Title>
        {intl.formatMessage<React.ReactNode>(
          {
            id: `integration.setup.categories.${categoryName}.sentence`,
            defaultMessage: " "
          },
          {
            a: chunks => (
              <ActionBtn
                variant="link"
                target="_blank"
                href={intl.formatMessage({ id: learnMoreLinkId })}
                rel="noreferrer"
              >
                {chunks}
              </ActionBtn>
            )
          }
        )}
      </S.Header>
      <S.List>
        {sortedList.map(elt => (
          <IntegrationItem
            allowEdit={
              getEnablement
                ? getEnablement(elt) && getCanEditGitConnection(elt)
                : getCanEditGitConnection(elt)
            }
            key={`int-${elt.type}`}
            integration={elt}
            isConnected={isGitConnected(elt)}
            onSelect={() => onSelect(elt)}
          />
        ))}
      </S.List>

      {currentGit && modal.open && (
        <Modal
          isOpen={modal.open}
          onOpenChange={() => setModal({ open: false })}
          handleClose={() => setModal({ open: false })}
          id="modal-replace-git-service"
        >
          <Typography.Title tag="h2">
            {intl.formatMessage({
              id: `integration.setup.modal.git.title`,
              defaultMessage: "Replace your Git service"
            })}
          </Typography.Title>
          <Typography.BodyText>
            {intl.formatMessage<React.ReactNode>(
              {
                id: "integration.setup.modal.git.sentence"
              },
              {
                b: chunks => <b>{chunks}</b>,
                current: intl.formatMessage({
                  id: `integration.type.${currentGit?.type}`
                }),
                new: intl.formatMessage({
                  id: `integration.type.${modal?.type}`
                })
              }
            )}
          </Typography.BodyText>
          <ButtonWrapper spacing="modal" justifyContent="end">
            <Button
              analyticId="cancel"
              variant="secondary"
              onClick={() => setModal({ open: false })}
            >
              {intl.formatMessage({ id: "cancel" })}
            </Button>
            <Button
              id="delete-modal-replace-git-service-confirm-modal-button"
              onClick={onReplaceConfirm}
              analyticId="delete"
            >
              {capitalize(intl.formatMessage({ id: "delete" }))}
            </Button>
          </ButtonWrapper>
        </Modal>
      )}
    </S.Wrapper>
  );
};

export default IntegrationCategory;
